import { useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const useRequest = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const { logout } = useAuth();

    const sendRequest = useCallback(async (config, attempts = 0, delayMs = 1000) => {
        setLoading(true);
        setError(null);

        let lastError = null;

        for (let attempt = 0; attempt <= attempts; attempt++) {
            try {
                const response = await axiosInstance(config);
                
                // Check for Access Denied in successful response (status 200)
                if (response.data && response.data.status === 'error' && response.data.message === 'Access Denied') {
                    toast.error('Session expired. Please log in again.');
                    if (logout) {
                        logout();
                    }
                    break; // Break immediately on Access Denied
                }
                
                setData(response);
                return response; // Return the response if successful
            } catch (err) {
                lastError = err;
                if (config.url) err["url"] = config.url; // Attach URL for context

                // Handle Access Denied from axios interceptor
                if (err.status === 'error' && err.message === 'Access Denied') {
                    toast.error('Session expired. Please log in again.');
                    if (logout) {
                        logout();
                    }
                    break; // Break immediately on Access Denied
                }

                // Handle unauthorized access (401)
                if (err.response?.status === 401) {
                    toast.error('Session expired. Please log in again.');
                    if (logout) {
                        logout();
                    }
                    break; // Break immediately on 401
                }

                // Retry only for status 500
                if (err.response?.status === 500 && attempt < attempts) {
                    console.log(`Retrying request to ${config.url}... Attempt ${attempt + 1} of ${attempts}`);
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                } else {
                    console.log(err);
                    break; // Break if not retryable or max attempts reached
                }
            } finally {
                setLoading(false);
            }
        }

        setError(lastError);
        return lastError; // Return the last error after all attempts
    }, [logout]);

    return { sendRequest, data, error, loading };
};

export default useRequest;
